import React, { useEffect, useState } from "react";
import { ExclamationTriangleIcon, TruckIcon, ArchiveBoxIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleSolidIcon } from "@heroicons/react/24/solid";
import { Link } from "react-router-dom";
import { fetchItemList, handleItem as handleItemApi } from "../../../api/inventory/itemLists";
import ItemHandlingDialog from "./item-handling-dialog";
import { markItemHandlingUpdated } from "../../../utils/itemHandlingUpdates";
import { isItemHandled, itemHandlingDestination } from "../../../utils/itemHandlingStatus";
import { normalizeError } from "../../../operationFeedback";

/**
 * Generic ItemHandlingList component that can be used for both reports and work orders
 * @param {Object} props
 * @param {Object} props.itemList - The item list object with item_list_items
 * @param {boolean} props.isLoading - Loading state
 * @param {Function} props.onItemHandled - Callback when an item is handled
 * @param {boolean} props.showBanner - Whether to show the banner message (default: true)
 */
const ItemHandlingList = ({ itemList, isLoading = false, onItemHandled, showBanner = true }) => {
  const [handlingItem, setHandlingItem] = useState(null);
  const [dialogProblem, setDialogProblem] = useState("");
  const [needsReload, setNeedsReload] = useState(false);
  const [freshList, setFreshList] = useState(null);
  useEffect(() => { setFreshList(null); }, [itemList]);
  const visibleList = freshList?.id === itemList?.id ? freshList : itemList;
  const [handlingDialog, setHandlingDialog] = useState({
    open: false,
    itemListItem: null,
    action: null
  });

  const handleItemAction = (itemListItem, action) => {
    setDialogProblem("");
    setNeedsReload(false);
    setHandlingDialog({
      open: true,
      itemListItem,
      action
    });
  };

  const handleDialogConfirm = async (handlingData) => {
    setHandlingItem(handlingDialog.itemListItem.id);

    try {
      // Handle the specified quantity
      await handleItemApi(
        itemList.id,
        handlingDialog.itemListItem.id,
        handlingData.action,
        handlingData.storageId,
        handlingData.quantity
      );
      setHandlingDialog({ open: false, itemListItem: null, action: null });
      markItemHandlingUpdated(itemList?.module_param);
      try {
        const refreshed = await fetchItemList(itemList.id);
        setFreshList(refreshed.data);
        if (onItemHandled) onItemHandled('REFETCH_NEEDED', refreshed.data);
      } catch (refreshError) {
        console.error('Item was handled, but the list refresh failed:', refreshError);
        alert('Varen ble håndtert, men listen kunne ikke oppdateres. Last siden på nytt før du fortsetter.');
      }
    } catch (error) {
      const problem = normalizeError(error);
      setDialogProblem(problem.safeMessage);
      setNeedsReload(problem.outcome !== 'not_applied' || problem.httpStatus === 409);
    } finally {
      setHandlingItem(null);
    }
  };

  const handleDialogCancel = () => {
    if (handlingItem !== null || needsReload) return;
    setHandlingDialog({ open: false, itemListItem: null, action: null });
  };

  const reloadAfterUncertainHandling = async () => {
    try {
      const refreshed = await fetchItemList(itemList.id);
      setFreshList(refreshed.data);
      setHandlingDialog({ open: false, itemListItem: null, action: null });
      setDialogProblem("");
      setNeedsReload(false);
      if (onItemHandled) onItemHandled('REFETCH_NEEDED', refreshed.data);
    } catch (error) {
      setDialogProblem(normalizeError(error).safeMessage);
    }
  };

  const getItemStatus = (row) => {
    const orderItem = row.order_item || {};
    if (row.handling_action === 'return_to_storage' ||
      (!row.handling_action && orderItem.returned_to_storage)) {
      const storageName = itemHandlingDestination(row) || 'lager';
      return {
        text: `Returnert til ${storageName}`,
        color: 'bg-green-100 text-green-800',
        icon: ArchiveBoxIcon
      };
    } else if (row.handling_action === 'move_to_storage') {
      return {
        text: `Flyttet til ${itemHandlingDestination(row) || 'lager'}`,
        color: 'bg-blue-100 text-blue-800',
        icon: ArchiveBoxIcon
      };
    } else if (row.handling_action === 'move_to_vehicle' || (!row.handling_action && orderItem.left_in_vehicle)) {
      const vehicleName = itemHandlingDestination(row) || 'kjøretøy';
      return {
        text: `Flyttet til ${vehicleName}`,
        color: 'bg-blue-100 text-blue-800',
        icon: TruckIcon
      };
    } else {
      return {
        text: 'Må håndteres',
        color: 'bg-yellow-100 text-yellow-800',
        icon: ExclamationTriangleIcon
      };
    }
  };
  if (isLoading) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
        <p className="text-gray-500">Laster varehåndteringsliste...</p>
      </div>
    );
  }

  // Don't show anything if no list exists
  if (!visibleList) {
    return null;
  }

  // Get all items with their handling status
  const allItems = visibleList.item_list_items || [];

  // Filter out already handled items for the action buttons
  const unhandledItems = allItems.filter((itemListItem) => {
    return !isItemHandled(itemListItem);
  });

  // Show message if list exists but has no items at all
  if (allItems.length === 0) {
    return null; // Don't show empty list
  }

  return (
    <div className="space-y-4 mt-6">
      {showBanner && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start flex-1">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-blue-800 mb-2">
                  Varehåndteringsliste opprettet
                </h3>
              <p className="text-sm text-blue-700">
                {unhandledItems.length > 0
                  ? `${unhandledItems.length} av ${allItems.length} produkter må håndteres. Listen oppdateres automatisk når vareforbruket endres.`
                  : `${allItems.length} produkter er håndtert og klare for neste steg.`
                }
              </p>
              </div>
            </div>
            <div className="ml-4 flex-shrink-0">
              <Link
                to={`/dashboard/inventory/item-handling/${visibleList.id}`}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-md"
              >
                Håndter varer
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                Produkt
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:hidden">
                Produkt/Info
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                Antall
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                Enhet
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Handling
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {allItems.map((itemListItem) => {
              const orderItem = itemListItem.order_item || itemListItem;
              const product = orderItem?.product || {};

              return (
                <tr key={itemListItem.id || orderItem?.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="font-medium text-gray-900">
                        {product?.displayTitle ||
                          product?.title ||
                          orderItem?.title ||
                          `Produkt ${orderItem?.product_id || "Ukjent"}`}
                      </div>
                      {product?.item_number && (
                        <div className="text-sm text-gray-500">
                          Art.nr: {product.item_number}
                        </div>
                      )}
                      {/* Mobile view: Show quantity, unit inline */}
                      <div className="sm:hidden mt-2 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-700 text-center">
                            Antall: {itemListItem.quantity || 0} {orderItem?.unit?.title || ""}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center hidden sm:table-cell">
                    <span className="font-medium">{itemListItem.quantity || 0}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center hidden sm:table-cell">
                    <span className="text-sm text-gray-600">
                      {orderItem?.unit?.title || "-"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center hidden md:table-cell">
                    {(() => {
                      const status = getItemStatus(itemListItem);
                      const StatusIcon = status.icon;
                      return (
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${status.color}`}>
                          <StatusIcon className="h-4 w-4 mr-1" />
                          {status.text}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {isItemHandled(itemListItem) ? (
                      <CheckCircleSolidIcon className="h-5 w-5 text-green-600 mx-auto" />
                    ) : (
                      <div className="flex items-center justify-center space-x-1">
                        <button
                          onClick={() => handleItemAction(itemListItem, 'return_to_storage')}
                          disabled={handlingItem === itemListItem.id}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded disabled:opacity-50"
                          title="Returner til lager"
                        >
                          <ArchiveBoxIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleItemAction(itemListItem, 'move_to_vehicle')}
                          disabled={handlingItem === itemListItem.id}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded disabled:opacity-50"
                          title="Flytt til kjøretøy"
                        >
                          <TruckIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleItemAction(itemListItem, 'move_to_storage')}
                          disabled={handlingItem === itemListItem.id}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 hover:bg-indigo-200 rounded disabled:opacity-50"
                          title="Flytt til annet lager"
                          aria-label="Flytt til annet lager"
                        >
                          <ArrowRightIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Item Handling Dialog */}
      <ItemHandlingDialog
        open={handlingDialog.open}
        itemListItem={handlingDialog.itemListItem}
        action={handlingDialog.action}
        onConfirm={handleDialogConfirm}
        onCancel={handleDialogCancel}
        busy={handlingItem !== null}
        serverError={dialogProblem}
        needsReload={needsReload}
        onReload={reloadAfterUncertainHandling}
      />

      {visibleList.status && (
        <div className="mt-4 text-sm text-gray-600">
          <span className="font-medium">Status:</span>{" "}
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              visibleList.status === "draft"
                ? "bg-gray-100 text-gray-800"
                : visibleList.status === "pending_approval"
                ? "bg-yellow-100 text-yellow-800"
                : visibleList.status === "approved"
                ? "bg-green-100 text-green-800"
                : visibleList.status === "rejected"
                ? "bg-red-100 text-red-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {visibleList.status === "draft"
              ? "Utkast"
              : visibleList.status === "pending_approval"
              ? "Venter på godkjenning"
              : visibleList.status === "approved"
              ? "Godkjent"
              : visibleList.status === "rejected"
              ? "Avvist"
              : visibleList.status}
          </span>
        </div>
      )}
    </div>
  );
};

export default ItemHandlingList;



